import { useLocation } from 'react-router-dom'

import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { featureOn } from '@/lib/features'
import { entriesToday, hasToday, localDayIso, meetingsToday, startOfLocalDay } from '@/lib/today'
import type { ScheduleEntry, Workshop } from '@/lib/types'

/**
 * How far back to look for a meeting that is still running today. A workshop
 * that began last Monday and ends tomorrow is happening now; one that began a
 * fortnight ago is a data-entry accident, not an offsite.
 */
const MULTI_DAY_LOOKBACK_DAYS = 7

/**
 * Is there anything on today? — the question the sidebar asks.
 *
 * Two small reads, deliberately separate from the Today page's own query. The
 * page needs the meetings, the plan, the links and the topics behind them; the
 * nav needs a yes or a no, and making the sidebar carry the page's payload on
 * every single navigation is a worse trade than asking twice on the one day it
 * matters.
 *
 * Re-asked on navigation, because the answer changes from inside the app: a
 * lead who has just put today's retro on the Schedule should get the tab
 * without being told to reload. Switching teams re-asks for the same reason.
 *
 * Both halves respect the team's section switches (0044). A team that has
 * turned Workshops off should not be told about a workshop, and the Today page
 * would have nowhere to send them if it were.
 */
export function useTodayGate(): { has: boolean; loading: boolean } {
  const { currentTeam } = useAuth()
  const teamId = currentTeam?.id
  const { pathname } = useLocation()
  const workshopsOn = featureOn(currentTeam, 'workshops_enabled')
  const scheduleOn = featureOn(currentTeam, 'schedule_enabled')

  const { data, loading } = useQuery(async () => {
    if (!teamId) return false
    const now = new Date()
    const since = startOfLocalDay(now)
    since.setDate(since.getDate() - MULTI_DAY_LOOKBACK_DAYS)

    const [meetings, entries] = await Promise.all([
      workshopsOn
        ? sb()
            .from('workshops')
            .select('id, starts_at, ends_at, status')
            .eq('team_id', teamId)
            .gte('starts_at', since.toISOString())
        : Promise.resolve({ data: [], error: null }),
      scheduleOn
        ? sb()
            .from('schedule_entries')
            .select('id, scheduled_on, status, display_order, created_at')
            .eq('team_id', teamId)
            .eq('scheduled_on', localDayIso(now))
        : Promise.resolve({ data: [], error: null }),
    ])
    // A failed read is not a reason to take the tab away — it is a reason to
    // leave it absent and let the pages themselves report the error. Either
    // query coming back empty is the same answer as "nothing today".
    if (meetings.error || entries.error) return false
    return hasToday(
      meetingsToday((meetings.data ?? []) as Workshop[], now),
      entriesToday((entries.data ?? []) as ScheduleEntry[], now),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, workshopsOn, scheduleOn, pathname])

  return { has: data === true, loading }
}
